<script setup lang="ts">
import { ref, computed } from 'vue'
import { useQuasar } from 'quasar'
import { useAuthStore } from 'stores/auth-store'
import { useFavoriteProgrammeStore } from 'stores/favorite_programme-store'
import { useRoute, useRouter } from 'vue-router'
import { login, logout } from 'src/utils/keycloakManager'
import { useLocaleStore } from 'src/stores/locale-store'
import { storeToRefs } from 'pinia'
import ProgrammeSearchModal from './ProgrammeSearchModal.vue'
const $q = useQuasar()

const favoriteProgrammeStore = useFavoriteProgrammeStore()
const authStore = useAuthStore()
const localeStore = useLocaleStore()
const route = useRoute()
const router = useRouter()

const drawerLeft = ref(false)

const baseUrl = import.meta.env.BASE_URL
const originUrl = import.meta.env.VITE_REDIRECT_URI

const { locale } = storeToRefs(localeStore)
const isAuthenticated = computed(() => authStore.isAuthenticated)
const languageButtonIcon = computed(() =>
  locale.value === 'en-US' ? `${baseUrl}icons/greek-lang.png` : `${baseUrl}icons/english-lang.png`
)

async function selfLogout() {
  authStore.clearAuth()
  await logout()
}

const toggleLanguage = async () => {
  const current = route.params.lang as 'en' | 'el'
  const next = current === 'en' ? 'el' : 'en'

  // 1. Καθαρίζουμε το path από το παλιό locale prefix
  const newPath = route.path.replace(/^\/(en|el)/, '')

  await router.push({
    path: `/${next}${newPath}`,
    query: route.query, // <--- Αυτό κρατάει τα params (π.χ. ?search=123)
  })
}

const mainTabs = [
  { to: { name: 'Home' }, icon: 'home', exact: true, label: 'navbar.home' },
  {
    to: { name: 'Programmes', query: { language: 'English' } },
    icon: 'book',
    exact: false,
    label: 'navbar.programs',
  },
  { to: { name: 'Universities' }, icon: 'school', exact: false, label: 'navbar.institutions' },
]

const accountMenuItems = computed(() => {
  if (!isAuthenticated.value) {
    return [
      {
        label: 'navbar.menu.login_applicant',
        icon: 'school',
        action: () => void authStore.openLoginModal($q.cookies, route.path),
        to: undefined,
        href: undefined,
      },
      {
        label: 'navbar.menu.login_agent',
        icon: 'assignment_ind',
        action: () => void login(),
        to: undefined,
        href: undefined,
      },
      {
        label: 'navbar.menu.login_secretary',
        icon: 'local_library',
        action: undefined,
        to: undefined,
        href: `${originUrl}/secretary/`,
      },
      {
        label: 'navbar.menu.login_evaluator',
        icon: 'account_circle',
        action: undefined,
        to: undefined,
        href: `${originUrl}/evaluator/`,
      },
    ]
  } else {
    if (authStore.isApplicant) {
      return [
        {
          label: 'navbar.menu.profile',
          icon: 'badge',
          action: undefined,
          to: { name: 'Applicant Profile' },
          href: undefined,
        },
        {
          label: 'navbar.menu.fav_progs',
          icon: 'favorite',
          action: () => favoriteProgrammeStore.openModal(),
          to: undefined,
          href: undefined,
        },
        {
          label: 'navbar.menu.applications',
          icon: 'fact_check',
          action: undefined,
          to: { name: 'Applications', query: { status: 'Finalized' } },
          href: undefined,
        },
        {
          label: 'navbar.menu.logout',
          icon: 'logout',
          action: () => void selfLogout(),
          to: undefined,
          href: undefined,
        },
      ]
    } else if (authStore.isAgent) {
      const items = []

      if (authStore.isParentAgent) {
        items.push({
          label: 'navbar.menu.agents',
          icon: 'groups_3',
          to: { name: 'Agent Agents' },
        })
      }

      // Use the spread operator (...) to keep the array flat
      items.push(
        {
          label: 'navbar.menu.applicants',
          icon: 'group',
          to: { name: 'Agent Applicants' },
        },
        {
          label: 'navbar.menu.applications',
          icon: 'fact_check',
          to: { name: 'Applications', query: { status: 'Finalized' } },
        },
        {
          label: 'navbar.menu.logout',
          icon: 'logout',
          action: () => void selfLogout(),
        }
      )

      return items
    } else if (authStore.isSecretary || authStore.isHelpdesk) {
      return [
        {
          label: 'navbar.menu.profile',
          icon: 'badge',
          action: undefined,
          to: undefined,
          href: `${originUrl}/secretary/secretary-profile`,
        },
        {
          label: 'navbar.menu.programmes',
          icon: 'book',
          action: undefined,
          to: undefined,
          href: `${originUrl}/secretary/programmes`,
        },
        {
          label: 'navbar.menu.calls',
          icon: 'integration_instructions',
          action: undefined,
          to: undefined,
          href: `${originUrl}/secretary/calls`,
        },
        {
          label: 'navbar.menu.logout',
          icon: 'logout',
          action: () => void selfLogout(),
          to: undefined,
          href: undefined,
        },
      ]
    } else if (authStore.isEvaluator) {
      return [
        {
          label: 'navbar.menu.profile',
          icon: 'badge',
          action: undefined,
          to: undefined,
          href: `${originUrl}/evaluator/profile`,
        },
        {
          label: 'navbar.menu.calls',
          icon: 'integration_instructions',
          action: undefined,
          to: undefined,
          href: `${originUrl}/evaluator/calls`,
        },
        {
          label: 'navbar.menu.logout',
          icon: 'logout',
          action: () => void selfLogout(),
          to: undefined,
          href: undefined,
        },
      ]
    } else {
      return [
        {
          label: 'navbar.menu.logout',
          icon: 'logout',
          action: () => void selfLogout(),
          to: undefined,
          href: undefined,
        },
      ]
    }
  }
})
</script>

<template>
  <div class="q-pb-sm q-pt-lg">
    <div class="container">
      <div
        class="bg-white text-primary row justify-between flex items-center shadow-2 rounded-borders q-px-md"
      >
        <!-- LOGO -->
        <q-btn flat dense no-wrap :to="{ name: 'Home' }" class="q-ma-none">
          <img style="height: 60px" src="~assets/sig-logo.png" />
        </q-btn>

        <!-- DESKTOP TABS -->
        <q-tabs v-if="$q.screen.gt.sm" align="center" inline-label>
          <q-route-tab
            no-caps
            v-for="(tab, index) in mainTabs"
            :exact="tab.exact"
            :to="tab.to"
            :key="index"
            :icon="tab.icon"
            :label="$t(tab.label)"
          />
        </q-tabs>

        <div class="q-gutter-x-sm flex items-center">
          <programme-search-modal />

          <!-- LANGUAGE BUTTON ALWAYS VISIBLE -->
          <q-btn v-if="$q.screen.gt.sm" @click="toggleLanguage" size="10px" no-caps round>
            <q-img :src="languageButtonIcon" />
          </q-btn>

          <!-- DESKTOP ACCOUNT DROPDOWN -->
          <q-btn-dropdown
            v-if="$q.screen.gt.sm"
            rounded
            no-caps
            color="secondary"
            text-color="primary"
            :icon="isAuthenticated ? 'account_circle' : 'login'"
            :label="$t(isAuthenticated ? 'navbar.menu.account' : 'navbar.menu.login')"
          >
            <q-list>
              <q-item
                v-for="item in accountMenuItems"
                :key="item.label"
                clickable
                v-close-popup
                v-bind="item.to ? { to: item.to } : item.href ? { href: item.href } : {}"
                @click="item.action && item.action()"
              >
                <q-item-section avatar>
                  <q-icon :name="item.icon" color="primary" size="30px" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ $t(item.label) }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>

          <!-- MOBILE HAMBURGER BUTTON -->
          <q-btn v-if="$q.screen.lt.md" flat round dense icon="menu" @click="drawerLeft = true" />
        </div>
      </div>
      <!-- MOBILE DRAWER -->
      <q-drawer v-model="drawerLeft" side="left" bordered class="bg-grey-2">
        <q-scroll-area class="fit">
          <q-list>
            
            <!-- MAIN TABS -->
            <q-item-label header class="q-mt-sm">
              {{ $t('navbar.menu.navigation') }}
            </q-item-label>

            <q-item
              v-for="(tab, index) in mainTabs"
              :key="index"
              :exact="tab.exact"
              clickable
              :to="tab.to"
              v-close-popup
            >
              <q-item-section avatar>
                <q-icon :name="tab.icon" />
              </q-item-section>
              <q-item-section>{{ $t(tab.label) }}</q-item-section>
            </q-item>
            <!-- ACCOUNT MENU ITEMS -->
            <q-item-label header class="q-mt-md">
              {{ $t('navbar.menu.account') }}
            </q-item-label>

            <q-item
              v-for="item in accountMenuItems"
              :key="item.label"
              clickable
              v-close-popup
              v-bind="item.to ? { to: item.to } : item.href ? { href: item.href } : {}"
              @click="item.action && item.action()"
            >
              <q-item-section avatar>
                <q-icon :name="item.icon" />
              </q-item-section>
              <q-item-section>{{ $t(item.label) }}</q-item-section>
            </q-item>

            <!-- LANGUAGE SWITCH -->
            <q-separator spaced />
            <q-item clickable @click="toggleLanguage">
              <q-item-section avatar>
                <q-img style="width: 30px" :src="languageButtonIcon" />
              </q-item-section>
              <q-item-section>
                {{ locale === 'en-US' ? 'Ελληνικά' : 'English' }}
              </q-item-section>
            </q-item>
          </q-list>
        </q-scroll-area>
      </q-drawer>
    </div>
  </div>
</template>
